/**
 * Scene State Atoms - Sprint 15-16
 * Jotai atoms for multi-scene recording state management
 */

import { atom } from 'jotai';
import { 
  Scene, 
  MultiSceneProject, 
  SceneRecordingState, 
  SceneStitchingProgress 
} from '../../../types/scene';
import logger from '../utils/logger';

export const activeProjectAtom = atom<MultiSceneProject | null>(null);

export const isProjectLoadingAtom = atom<boolean>(false);

export const currentSceneAtom = atom<Scene | null>(null);

export const sceneListAtom = atom<Scene[]>([]);

export const sceneRecordingStateAtom = atom<SceneRecordingState>({
  currentSceneIndex: 0,
  isRecording: false,
  isPaused: false,
  recordedScenes: 0,
  totalScenes: 0,
});

export const stitchingProgressAtom = atom<SceneStitchingProgress>({
  isStitching: false,
  progress: 0,
  currentScene: undefined,
  totalScenes: undefined,
  status: 'idle',
  error: undefined,
});

export const isSceneTimelineVisibleAtom = atom<boolean>(false);

export const selectedSceneIdAtom = atom<string | null>(null);

export const isMultiSceneModeAtom = atom<boolean>(false);

// Tracks whether the current project is in blank mode (no limits)
// This is set by CanvasRecorder based on URL param or project metadata
export const isBlankModeAtom = atom<boolean>(false);

export type LayoutMode = 'slides';
export const layoutModeAtom = atom<LayoutMode>('slides');

export const currentSceneIndexAtom = atom(
  (get) => get(sceneRecordingStateAtom).currentSceneIndex,
  (get, set, newIndex: number) => {
    const state = get(sceneRecordingStateAtom);
    set(sceneRecordingStateAtom, { ...state, currentSceneIndex: newIndex });
  }
);

export const recordedScenesCountAtom = atom(
  (get) => get(sceneRecordingStateAtom).recordedScenes
);

export const totalScenesCountAtom = atom(
  (get) => get(sceneRecordingStateAtom).totalScenes
);

export const nextSceneAtom = atom(
  null,
  (get, set) => {
    const state = get(sceneRecordingStateAtom);
    const sceneList = get(sceneListAtom);
    
    if (state.currentSceneIndex < sceneList.length - 1) {
      const nextIndex = state.currentSceneIndex + 1;
      const nextScene = sceneList[nextIndex];
      set(sceneRecordingStateAtom, { ...state, currentSceneIndex: nextIndex });
      set(currentSceneAtom, nextScene);
      set(selectedSceneIdAtom, nextScene.id);
    }
  }
);

export const previousSceneAtom = atom(
  null,
  (get, set) => {
    const state = get(sceneRecordingStateAtom);
    const sceneList = get(sceneListAtom);
    
    if (state.currentSceneIndex > 0) {
      const prevIndex = state.currentSceneIndex - 1;
      set(sceneRecordingStateAtom, { ...state, currentSceneIndex: prevIndex });
      set(currentSceneAtom, sceneList[prevIndex]);
    }
  }
);

export const resetSceneStateAtom = atom(
  null,
  (get, set) => {
    set(activeProjectAtom, null);
    set(currentSceneAtom, null);
    set(sceneListAtom, []);
    set(sceneRecordingStateAtom, {
      currentSceneIndex: 0,
      isRecording: false,
      isPaused: false,
      recordedScenes: 0,
      totalScenes: 0,
    });
    set(stitchingProgressAtom, {
      isStitching: false,
      progress: 0,
      currentScene: undefined,
      totalScenes: undefined,
      status: 'idle',
      error: undefined,
    });
    set(isMultiSceneModeAtom, false);
  }
);

export const loadProjectIntoStateAtom = atom(
  null,
  (get, set, project: MultiSceneProject) => {
    // Get the current scene list to preserve local-only statuses (pending_upload, pending_confirmation)
    const currentSceneList = get(sceneListAtom);
    const currentScene = get(currentSceneAtom);
    
    // Sort scenes by order field to ensure correct display sequence
    const sortedScenes = [...project.scenes].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    
    // Merge: preserve local pending_upload/pending_confirmation statuses that aren't in the database
    const mergedScenes = sortedScenes.map(scene => {
      const existingScene = currentSceneList.find(s => String(s.id) === String(scene.id));
      if (existingScene && (existingScene.status === 'pending_upload' || existingScene.status === 'pending_confirmation')) {
        // Preserve the local status and videoChunks if the DB scene is still pending
        if (scene.status === 'pending' || !scene.status) {
          logger.info('[loadProjectIntoStateAtom] Preserving local status:', {
            sceneId: scene.id,
            localStatus: existingScene.status,
            dbStatus: scene.status
          });
          return { 
            ...scene, 
            status: existingScene.status,
            videoChunks: existingScene.videoChunks || scene.videoChunks
          };
        }
      }
      return scene;
    });
    
    set(activeProjectAtom, { ...project, scenes: mergedScenes });
    set(sceneListAtom, mergedScenes);
    
    // Preserve current scene selection if it exists in the new list, otherwise use first scene
    let newCurrentScene = mergedScenes[0] || null;
    if (currentScene) {
      const existingCurrent = mergedScenes.find(s => String(s.id) === String(currentScene.id));
      if (existingCurrent) {
        newCurrentScene = existingCurrent;
      }
    }
    set(currentSceneAtom, newCurrentScene);
    
    // Also set selectedSceneId to keep slides rail in sync
    if (newCurrentScene) {
      set(selectedSceneIdAtom, newCurrentScene.id);
    }
    
    // LOGGING: Track when currentScene is set during project load
    logger.info('[loadProjectIntoStateAtom] Setting currentScene:', {
      projectId: project.id,
      projectTitle: project.title,
      currentSceneId: newCurrentScene?.id,
      currentSceneTitle: newCurrentScene?.title,
      totalScenes: mergedScenes.length
    });
    
    set(sceneRecordingStateAtom, {
      currentSceneIndex: newCurrentScene ? mergedScenes.findIndex(s => s.id === newCurrentScene.id) : 0,
      isRecording: false,
      isPaused: false,
      recordedScenes: mergedScenes.filter(s => s.status === 'recorded').length,
      totalScenes: mergedScenes.length,
    });
    set(isMultiSceneModeAtom, true);
  }
);

export const updateSceneInListAtom = atom(
  null,
  (get, set, updatedScene: Scene) => {
    const sceneList = get(sceneListAtom);
    const updatedList = sceneList.map(scene => 
      scene.id === updatedScene.id ? updatedScene : scene
    );
    set(sceneListAtom, updatedList);
    
    const currentScene = get(currentSceneAtom);
    if (currentScene?.id === updatedScene.id) {
      set(currentSceneAtom, updatedScene);
    }
    
    const activeProject = get(activeProjectAtom);
    if (activeProject) {
      set(activeProjectAtom, { ...activeProject, scenes: updatedList });
    }
  }
);

export const markSceneRecordedAtom = atom(
  null,
  (get, set, params: { sceneId: string; duration: number }) => {
    const { sceneId, duration } = params;
    const sceneList = get(sceneListAtom);
    const updatedList = sceneList.map(scene => 
      scene.id === sceneId 
        ? { ...scene, status: 'recorded' as const, recordedAt: Date.now(), duration } 
        : scene
    );
    set(sceneListAtom, updatedList);
    
    const state = get(sceneRecordingStateAtom);
    set(sceneRecordingStateAtom, {
      ...state,
      recordedScenes: updatedList.filter(s => s.status === 'recorded').length,
    });
    
    const activeProject = get(activeProjectAtom);
    if (activeProject) {
      set(activeProjectAtom, { ...activeProject, scenes: updatedList });
    }
  }
);

export const updateProjectDurationAtom = atom(
  null,
  (get, set, params: { sceneId: string; duration: number }) => {
    const { sceneId, duration } = params;
    const sceneList = get(sceneListAtom);
    const activeProject = get(activeProjectAtom);
    
    if (!activeProject) {
      logger.warn('[updateProjectDurationAtom] No active project to update');
      return;
    }
    
    // Update the scene duration in the list
    const updatedList = sceneList.map(scene => 
      scene.id === sceneId ? { ...scene, duration } : scene
    );
    set(sceneListAtom, updatedList);
    
    // Recalculate total duration
    const totalDuration = updatedList.reduce((sum, s) => sum + s.duration, 0);
    
    // Update activeProject with new scenes and totalDuration
    set(activeProjectAtom, { 
      ...activeProject, 
      scenes: updatedList,
      totalDuration 
    });
    
    logger.info('[updateProjectDurationAtom] Project duration updated:', {
      sceneId,
      sceneDuration: duration,
      totalDuration,
      totalDurationSeconds: (totalDuration / 1000).toFixed(2)
    });
  }
);

// Unified Recording Preview State
export interface RecordingPreviewState {
  visible: boolean;
  mode: 'scene' | 'single';
  videoGuid: string | null;
  sceneId: string | null;
  scene: Scene | null;
  duration: number; // Duration in milliseconds calculated from recording timestamps
  s3Key: string | null; // S3 object key for saving videoUrl after upload
  micEnabled: boolean; // Whether microphone was enabled during recording
}

export const recordingPreviewAtom = atom<RecordingPreviewState>({
  visible: false,
  mode: 'scene',
  videoGuid: null,
  sceneId: null,
  scene: null,
  duration: 0,
  s3Key: null,
  micEnabled: false,
});

export const showRecordingPreviewAtom = atom(
  null,
  (get, set, config: { mode: 'scene' | 'single'; videoGuid?: string; sceneId?: string; scene?: Scene; duration?: number; s3Key?: string; micEnabled?: boolean }) => {
    // Phase 4.2: Set scene status to 'pending_upload' when opening preview (only for new recordings)
    // For scenes that are already 'pending_confirmation' or have s3Key, preserve their status
    const sceneIdToUpdate = config.sceneId || config.scene?.id;
    if (sceneIdToUpdate && config.mode === 'scene') {
      const sceneList = get(sceneListAtom);
      const existingScene = sceneList.find(s => s.id === sceneIdToUpdate);
      
      // Don't update status if:
      // 1. Scene is already 'pending_confirmation' (already uploaded to temp storage)
      // 2. We're passing s3Key (re-opening existing upload)
      const shouldPreserveStatus = existingScene?.status === 'pending_confirmation' || config.s3Key;
      
      const updatedList = sceneList.map(scene =>
        scene.id === sceneIdToUpdate
          ? { 
              ...scene, 
              status: shouldPreserveStatus ? scene.status : ('pending_upload' as const),
              recordedAt: shouldPreserveStatus ? scene.recordedAt : Date.now(),
              duration: config.duration || scene.duration || 0,
              micEnabled: config.micEnabled ?? scene.micEnabled ?? false
            }
          : scene
      );
      set(sceneListAtom, updatedList);
      
      // Update active project scenes as well
      const activeProject = get(activeProjectAtom);
      if (activeProject) {
        set(activeProjectAtom, { ...activeProject, scenes: updatedList });
      }
    }
    
    set(recordingPreviewAtom, {
      visible: true,
      mode: config.mode,
      videoGuid: config.videoGuid,
      sceneId: config.sceneId || null,
      scene: config.scene || null,
      duration: config.duration ?? 0, // Use nullish coalescing to handle duration: 0 correctly
      s3Key: config.s3Key || null,
      micEnabled: config.micEnabled ?? false,
    });
  }
);

export const hideRecordingPreviewAtom = atom(
  null,
  (get, set) => {
    set(recordingPreviewAtom, {
      visible: false,
      mode: 'scene',
      videoGuid: null,
      sceneId: null,
      scene: null,
      duration: 0,
      s3Key: null,
      micEnabled: false,
    });
  }
);

// Teleprompter global state - for auto-opening when scene is selected
export const isTeleprompterOpenAtom = atom<boolean>(false);

// Stitching modal visibility
export const isStitchingModalVisibleAtom = atom<boolean>(false);

// Add Scene modal visibility
export const addSceneModalVisibleAtom = atom<boolean>(false);

// Sprint 19+: Timeline Collapse & Cinema Mode
export const isTimelineCollapsedAtom = atom<boolean>(false);

// Cinema mode state - initialized from localStorage
const getInitialCinemaMode = (): boolean => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('dadan-cinema-mode');
    return saved === 'true';
  }
  return false;
};

const cinemaModeBaseAtom = atom<boolean>(getInitialCinemaMode());

// Public cinema mode atom with localStorage persistence
export const isCinemaModeAtom = atom(
  (get) => get(cinemaModeBaseAtom),
  (get, set, enabled: boolean) => {
    set(cinemaModeBaseAtom, enabled);
    if (typeof window !== 'undefined') {
      localStorage.setItem('dadan-cinema-mode', String(enabled));
    }
  }
);
